import React, { FC } from 'react'
import { Dialog, LoadingButton } from '../atoms'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { RPUser } from '@hazadapt-git/public-core-base'
import { makeStyles } from 'tss-react/mui'
import { format } from 'date-fns-tz'
import { ProfileDetailsForm } from './ProfileDetailsForm'
import { useFormikStateSync } from '../../lib/hooks/useFormikStateSync'
import { isUserInvited } from '../../lib/utils'
import { useOrganization } from '../../lib/hooks/useOrganization'
import { ProfileFormMode } from '../../lib/entities'

interface TeamMemberConfigModalProps {
    targetUser: RPUser
    activeUser: RPUser
    // Dialog props
    open?: boolean
    onClose(): void
    mode: ProfileFormMode.EDIT_MEMBER | ProfileFormMode.PERMISSIONS
}

export const TeamMemberConfigModal: FC<TeamMemberConfigModalProps> = (
    props: TeamMemberConfigModalProps
) => {
    const { classes: localClasses } = useLocalStyles()
    const { handleSaveUserChanges } = useOrganization()
    const { formikRef, FormikStateBridge } =
        useFormikStateSync<Partial<RPUser>>()

    const handleSubmit = async (values: Partial<RPUser>) => {
        await handleSaveUserChanges({
            ...props.targetUser,
            ...values,
        })
        props.onClose()
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            width="min(40rem, 90vw)"
            title={
                props.mode === 'edit-member'
                    ? 'Edit Team Member'
                    : 'Edit Permissions'
            }
            content={
                <div className={localClasses.container}>
                    <div className={localClasses.header}>
                        <Avatar
                            sx={{
                                width: {
                                    xs: '3.5rem',
                                    md: '4.75rem',
                                },
                                height: {
                                    xs: '3.5rem',
                                    md: '4.75rem',
                                },
                            }}
                            src={
                                props.targetUser.profile_picture_uri ??
                                undefined
                            }
                        />
                        <div>
                            <Typography variant="h4">
                                {props.targetUser.first_name}{' '}
                                {props.targetUser.last_name}
                            </Typography>
                            <Typography>{props.targetUser.title}</Typography>
                            {props.mode === 'permissions' && (
                                <Typography fontWeight={300} fontStyle="italic">
                                    {props.targetUser.email}
                                </Typography>
                            )}
                            {props.targetUser.date_created && (
                                <Typography
                                    fontWeight={500}
                                    fontSize="1.125rem"
                                    mt="0.5rem"
                                >
                                    {isUserInvited(props.targetUser)
                                        ? 'Invited'
                                        : 'Added'}{' '}
                                    {format(
                                        new Date(props.targetUser.date_created),
                                        'MMMM d, y'
                                    )}
                                </Typography>
                            )}
                        </div>
                    </div>
                    <ProfileDetailsForm
                        formikRef={formikRef}
                        FormikStateBridge={FormikStateBridge}
                        activeUser={props.activeUser}
                        targetUser={props.targetUser}
                        onSubmit={handleSubmit}
                        mode={props.mode}
                    />
                </div>
            }
            ctas={[
                <Button
                    color="secondary"
                    variant="outlined"
                    onClick={props.onClose}
                    key="cancel"
                >
                    Cancel
                </Button>,
                <LoadingButton
                    color="primary"
                    variant="contained"
                    disabled={
                        !formikRef.current?.dirty || !formikRef.current?.isValid
                    }
                    onClickAsync={async () =>
                        await formikRef.current?.submitForm?.()
                    }
                    key="save-changes"
                >
                    Save Changes
                </LoadingButton>,
            ]}
        />
    )
}

const useLocalStyles = makeStyles()({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        gap: '0.75rem',
    },
})
